// Interaction (decision 0044: "the nearest valid interactable by distance and facing gets a prompt; priorities by
// kind"; phase C, gate C5). The game registers the things the player can use (a sign, a door, a lever, a herb, an
// NPC to talk to), each with a position, a reach, an optional cone it must be used from, a priority and a verb; each
// sim step the chooser picks the one the prompt shows: among the valid ones (in reach, in height, the actor inside
// the thing's cone, the thing inside the actor's facing cone or very close), the nearest by distance weighted by how
// far off the actor's facing it is; scores within a tie band count as level, and the higher priority takes it.
// Pressing the game's interact action fires the chosen one. Pure logic over plain data, deterministic; the prompt's
// glyph and text are the game's (its input glyphs and strings).
import { wrapAngle } from "./yaw";

/** A point in the world. */
export interface InteractPoint {
  x: number;
  y: number;
  z: number;
}

/** One thing the player can use. */
export interface Interactable {
  id: string;
  /** What pressing does, as the game names it (a string id's key: "read", "open", "pull", "gather", "talk"). */
  verb: string;
  /** Its use point. */
  pos: InteractPoint;
  /** How close the actor must stand, across the ground (m). */
  radius: number;
  /** Used only from inside this cone: the thing's facing (yaw, 0 faces +Z) and the cone's half angle (rad). A sign
   *  read from the front, a door from either side as two entries. Absent: from anywhere. */
  cone?: { yaw: number; half: number };
  /** Higher wins a tie (decision 0044's priority by kind: talk above pick up above open, as the game sets it). */
  priority: number;
  /** False: never chosen (a gathered herb waiting to regrow, a door mid-swing). Default true. */
  enabled?: boolean;
}

/** Who is looking for something to use: feet and facing. */
export interface InteractActor {
  pos: InteractPoint;
  /** Yaw (rad; 0 faces +Z, the kernel's convention). */
  yaw: number;
}

/** The chooser's tuning (the game's values). */
export interface InteractTuning {
  /** Half angle of the actor's facing cone (rad): a thing outside it is not chosen unless within `near`. */
  facingHalf: number;
  /** Within this distance (m), facing doesn't matter. */
  near: number;
  /** How much a thing off to the side counts as further: score = distance × (1 + facingWeight × (1 − cos angle)). */
  facingWeight: number;
  /** Scores within this many metres of the best count as level; the higher priority takes it. */
  tie: number;
  /** The most a thing's point may sit above or below the actor's feet (m). */
  height: number;
  /** Keep the current choice while its score is within this many metres of the best (the prompt doesn't flicker
   *  between two near things); 0 for none. A higher priority still takes over. */
  stick: number;
}

/** One valid candidate, scored. */
export interface InteractCandidate {
  id: string;
  verb: string;
  priority: number;
  /** Across the ground (m). */
  distance: number;
  /** Off the actor's facing (rad, 0..π). */
  angle: number;
  score: number;
}

/** What the chooser decided, and why. */
export interface InteractChoice {
  /** The chosen thing's id, or null. */
  id: string | null;
  verb: string | null;
  /** Every valid candidate, best score first. */
  ranked: InteractCandidate[];
  /** Things in reach that failed a test, with the test ("height", "cone", "facing", "disabled"). */
  rejected: { id: string; why: string }[];
}

/** Planted defects, beside what they break (tests only). */
export interface InteractFaults {
  /** Ties ignore priority: the nearest wins (the interaction spec's priority layouts must go red). */
  ignorePriority: boolean;
  /** The actor's facing is ignored (score by distance alone; nothing is rejected for facing). */
  ignoreFacing: boolean;
}

/** Score the things against an actor and pick one. `current` is last step's choice (for `stick`). */
export function chooseInteractable(actor: InteractActor, things: readonly Interactable[], t: InteractTuning, current: string | null = null, faults: Partial<InteractFaults> = {}): InteractChoice {
  const ranked: InteractCandidate[] = [];
  const rejected: { id: string; why: string }[] = [];
  const fx = Math.sin(actor.yaw);
  const fz = Math.cos(actor.yaw);
  for (const th of things) {
    const dx = th.pos.x - actor.pos.x;
    const dz = th.pos.z - actor.pos.z;
    const distance = Math.hypot(dx, dz);
    if (distance > th.radius) continue;
    if (th.enabled === false) {
      rejected.push({ id: th.id, why: "disabled" });
      continue;
    }
    if (Math.abs(th.pos.y - actor.pos.y) > t.height) {
      rejected.push({ id: th.id, why: "height" });
      continue;
    }
    if (th.cone && distance > 1e-6) {
      // the actor must stand in front of the thing: the thing's facing toward the actor within its half angle
      const off = Math.abs(wrapAngle(Math.atan2(-dx, -dz) - th.cone.yaw));
      if (off > th.cone.half) {
        rejected.push({ id: th.id, why: "cone" });
        continue;
      }
    }
    const cos = distance > 1e-6 ? (dx * fx + dz * fz) / distance : 1;
    const angle = Math.acos(Math.max(-1, Math.min(1, cos)));
    if (!faults.ignoreFacing && distance > t.near && angle > t.facingHalf) {
      rejected.push({ id: th.id, why: "facing" });
      continue;
    }
    const score = faults.ignoreFacing ? distance : distance * (1 + t.facingWeight * (1 - cos));
    ranked.push({ id: th.id, verb: th.verb, priority: th.priority, distance, angle, score });
  }
  ranked.sort((a, b) => a.score - b.score || b.priority - a.priority || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (!ranked.length) return { id: null, verb: null, ranked, rejected };
  const best = ranked[0]!.score;
  // the level ones (within the tie band): the highest priority, then the best score
  const level = ranked.filter((c) => c.score <= best + t.tie);
  let pick = faults.ignorePriority ? level[0]! : level.reduce((a, c) => (c.priority > a.priority ? c : a), level[0]!);
  // stickiness: keep last step's choice while it is close to the best and nothing outranks it
  const cur = current === null ? undefined : ranked.find((c) => c.id === current);
  if (cur && cur.id !== pick.id && t.stick > 0 && cur.score <= best + t.stick && (faults.ignorePriority || cur.priority >= pick.priority)) pick = cur;
  return { id: pick.id, verb: pick.verb, ranked, rejected };
}

/** What an interaction system keeps between steps (a kernel component: saves and hashes cover it). */
export interface InteractionState {
  /** The thing chosen last step (the prompt's), or null. */
  current: string | null;
  /** Fires so far. */
  fired: number;
}

/** A fired interaction. */
export interface InteractFire {
  id: string;
  verb: string;
}

/** The registry and the per-step choice. Register things once (or update them as they change); each step call
 *  `update` with the actor, then `fire` with whether interact was pressed. */
export class Interaction {
  /** Planted defects (tests only). */
  readonly faults: InteractFaults = { ignorePriority: false, ignoreFacing: false };
  private readonly things = new Map<string, Interactable>();
  private last: InteractChoice = { id: null, verb: null, ranked: [], rejected: [] };

  constructor(readonly tuning: InteractTuning) {}

  /** Add or replace a thing. */
  register(th: Interactable): void {
    this.things.set(th.id, { ...th, pos: { ...th.pos }, ...(th.cone ? { cone: { ...th.cone } } : {}) });
  }

  /** Remove a thing (unknown ids are ignored). */
  unregister(id: string): void {
    this.things.delete(id);
  }

  /** Change a thing's fields (enabled, verb, priority, pos). Throws on an unknown id. */
  set(id: string, patch: Partial<Omit<Interactable, "id">>): void {
    const th = this.things.get(id);
    if (!th) throw new Error(`interaction: no interactable "${id}"`);
    Object.assign(th, patch);
  }

  get(id: string): Interactable | undefined {
    return this.things.get(id);
  }

  /** Every registered thing, in the order added. */
  list(): Interactable[] {
    return [...this.things.values()];
  }

  /** Choose for this step; the state's `current` becomes the choice. */
  update(actor: InteractActor, state: InteractionState): InteractChoice {
    this.last = chooseInteractable(actor, [...this.things.values()], this.tuning, state.current, this.faults);
    state.current = this.last.id;
    return this.last;
  }

  /** The last `update`'s decision. */
  choice(): InteractChoice {
    return this.last;
  }

  /** Fire the current choice when `pressed`; returns what fired, or null. */
  fire(pressed: boolean, state: InteractionState): InteractFire | null {
    if (!pressed || state.current === null) return null;
    const th = this.things.get(state.current);
    if (!th || th.enabled === false) return null;
    state.fired++;
    return { id: th.id, verb: th.verb };
  }
}

